import { useRef, useState, type ReactNode } from 'react'
import { ArrowLeft, Plus, Sun, Target, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { cn } from '@/lib/utils'
import { NewWorkItemDialog } from '@/features/work/NewWorkItemDialog'
import { usePermission } from '@/features/permissions/usePermission'
import type { Board, Client, Member } from '@/types/entities'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { useWorkspace } from '@/features/workspace/WorkspaceProvider'
import { HAPPINESS, formatMinutes } from './hours'
import { flyNoteToDiary } from './flyNote'
import { useCheckCommitments, useGate, useMyOpenWork, useSavePlan, useSaveReview, type CommitmentCheck, type DiaryEntry, type Gate } from './api'

const NONE = 'none'
const CREATE = '__create__'
const dayLabel = (date: string) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
const weekday = (date: string) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' })
const greeting = () => {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

const TIME_CHOICES = [30, 60, 120, 180, 240]

/**
 * The start-of-day check-in: a few short, optional-feeling steps. First a look back at the last day that was planned,
 * then what today is for. Everything behind it opens when it is done, and teammates can read the diary.
 */
export function DiaryGate({ enabled }: { enabled: boolean }) {
  const gate = useGate(enabled)
  const data = gate.data
  const [reviewedDate, setReviewedDate] = useState<string | null>(null)
  const [planned, setPlanned] = useState(false)
  if (!enabled || !data) return null
  const needsReview = Boolean(data.toReview) && reviewedDate !== data.toReview?.date
  const needsPlan = !data.entry && !planned
  if (!needsReview && !needsPlan) return null
  return (
    <div role="dialog" aria-modal="true" aria-label="Daily diary" className="fixed inset-0 z-modal overflow-y-auto bg-background">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-linear-to-br from-info/15 via-background to-warning/20" />
      <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 size-96 rounded-full bg-warning/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-24 size-[28rem] rounded-full bg-brand-accent/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute right-1/4 top-10 size-64 rounded-full bg-info/25 blur-3xl" />
      <div className="relative mx-auto flex min-h-full w-full max-w-xl flex-col justify-center px-4 py-8">
        {needsReview && data.toReview ? (
          <ReviewFlow entry={data.toReview} logged={data.toReview.loggedMinutes} onDone={() => setReviewedDate(data.toReview?.date ?? null)} />
        ) : (
          <PlanFlow gate={data} onDone={() => setPlanned(true)} />
        )}
      </div>
    </div>
  )
}

function Frame({ cardRef, step, steps, kicker, title, sub, children, footer }: {
  cardRef: React.RefObject<HTMLDivElement | null>
  step: number
  steps: number
  kicker: string
  title: string
  sub?: string
  children: ReactNode
  footer: ReactNode
}) {
  return (
    <div ref={cardRef} className="rounded-3xl border border-border-soft bg-card/95 p-6 shadow-lg backdrop-blur sm:p-8">
      <div className="mb-5 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-tiny font-semibold uppercase tracking-wide text-brand-accent">
          <Sun className="size-3.5" aria-hidden /> {kicker}
        </p>
        <div className="flex gap-1.5" role="img" aria-label={`Step ${step + 1} of ${steps}`}>
          {Array.from({ length: steps }, (_, i) => (
            <span key={i} className={cn('h-1.5 w-6 rounded-full transition-colors', i <= step ? 'bg-brand-accent' : 'bg-muted')} />
          ))}
        </div>
      </div>
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {sub ? <p className="mt-1.5 text-sm text-text-secondary">{sub}</p> : null}
      <div className="mt-6">{children}</div>
      <div className="mt-8 flex items-center justify-between gap-3">{footer}</div>
    </div>
  )
}

function Chip({ active, onClick, children, label }: { active: boolean; onClick: () => void; children: ReactNode; label?: string }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-brand-soft',
      )}
    >
      {children}
    </button>
  )
}

/** Preset chips plus a custom number of minutes. */
function TimeChoice({ value, onChange, label }: { value: number; onChange: (m: number) => void; label: string }) {
  const preset = TIME_CHOICES.includes(value)
  const [custom, setCustom] = useState(!preset)
  const showInput = custom || !preset
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
      {TIME_CHOICES.map((m) => <Chip key={m} active={!showInput && value === m} onClick={() => { setCustom(false); onChange(m) }}>{formatMinutes(m)}</Chip>)}
      <Chip active={showInput} onClick={() => setCustom(true)}>Custom</Chip>
      {showInput ? (
        <span className="inline-flex items-center gap-1.5 text-sm text-text-secondary">
          <Input aria-label="Custom minutes" type="number" min={5} max={960} step={5} className="h-9 w-20" value={value || ''} onChange={(e) => onChange(Math.max(0, Math.min(960, Math.round(Number(e.target.value) || 0))))} />
          min
        </span>
      ) : null}
    </div>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return <Button type="button" variant="ghost" size="sm" onClick={onClick}><ArrowLeft className="size-3.5" /> Back</Button>
}

/* ------------------------------------------------------------------ looking back */

const MIN_STORY = 20

function ReviewFlow({ entry, logged, onDone }: { entry: DiaryEntry; logged: number; onDone: () => void }) {
  const save = useSaveReview()
  const { displayName } = useIdentity()
  const card = useRef<HTMLDivElement>(null)
  const steps = entry.outcomes.length > 0 ? ['mood', 'done', 'notes'] as const : ['mood', 'notes'] as const
  const [i, setI] = useState(0)
  const [done, setDone] = useState<Record<string, boolean>>(() => Object.fromEntries(entry.outcomes.map((o) => [o.id, o.done])))
  const [happiness, setHappiness] = useState<number | null>(null)
  const [moved, setMoved] = useState('')
  const [stuck, setStuck] = useState('')
  const [tomorrow, setTomorrow] = useState('')
  const [story, setStory] = useState('')
  const storyOk = story.trim().length >= MIN_STORY
  const doneCount = Object.values(done).filter(Boolean).length
  const which = steps[i] ?? 'mood'
  const last = i === steps.length - 1
  const first = (displayName || '').split(' ')[0]

  function submit() {
    if (happiness === null || !storyOk) return
    const rect = card.current?.getBoundingClientRect()
    save.mutate(
      { date: entry.date, happiness, moved, stuck, tomorrow, notes: story.trim(), outcomeDone: done },
      { onSuccess: () => { if (rect) flyNoteToDiary(rect, moved || entry.outcomes[0]?.text || 'Reviewed'); onDone() } },
    )
  }

  return (
    <Frame
      cardRef={card}
      step={i}
      steps={steps.length}
      kicker={`${greeting()}${first ? `, ${first}` : ''}`}
      title={which === 'mood' ? `How was ${weekday(entry.date)}?` : which === 'done' ? 'What did you get done?' : 'A couple of quick thoughts'}
      sub={which === 'mood' ? 'Tap the one that fits. It takes a second.' : which === 'done' ? 'Tick anything that is finished. Partly done still counts as progress.' : 'Tell us how your day went. The rest is optional.'}
      footer={
        <>
          {i > 0 ? <BackButton onClick={() => setI(i - 1)} /> : <span />}
          {which === 'mood' ? <span /> : last ? <Button onClick={submit} disabled={save.isPending || !storyOk}>Save and plan today</Button> : <Button onClick={() => setI(i + 1)}>Next</Button>}
        </>
      }
    >
      {which === 'mood' ? (
        <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label="How was that day">
          {HAPPINESS.map((h) => (
            <button
              key={h.value}
              type="button"
              role="radio"
              aria-checked={happiness === h.value}
              onClick={() => { setHappiness(h.value); setI(1) }}
              className={cn(
                'flex flex-col items-center gap-1 rounded-2xl border px-1 py-3 text-tiny transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:hover:translate-y-0',
                happiness === h.value ? 'border-primary bg-brand-soft font-semibold' : 'border-border bg-card hover:bg-brand-softer',
              )}
            >
              <span className="text-3xl" aria-hidden>{h.emoji}</span>
              {h.label}
            </button>
          ))}
        </div>
      ) : null}

      {which === 'done' ? (
        <div>
          <ul className="space-y-2">
            {entry.outcomes.map((o) => (
              <li key={o.id} className="flex items-start gap-3 rounded-xl border border-border-soft bg-card px-3 py-2.5">
                <Checkbox id={`rv-${o.id}`} checked={Boolean(done[o.id])} onCheckedChange={(v) => setDone((d) => ({ ...d, [o.id]: v === true }))} className="mt-0.5" />
                <label htmlFor={`rv-${o.id}`} className="flex-1 text-sm">{o.text}</label>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-text-secondary">
            {doneCount === 0 ? 'Nothing marked done. Say what got in the way on the next step.' : `${doneCount} of ${entry.outcomes.length} commitments done.`}
            {logged > 0 ? ` You spent ${formatMinutes(logged)} on the work timer.` : ''}
          </p>
        </div>
      ) : null}

      {which === 'notes' ? (
        <div className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="rv-story">How did your day go? (required)</Label>
            <Textarea id="rv-story" value={story} onChange={(e) => setStory(e.target.value)} rows={4} required aria-required="true" placeholder="Describe your day: what happened, how it felt, what you learned" />
            {!storyOk ? <p className="text-tiny text-text-muted">At least {MIN_STORY} characters. {Math.max(0, MIN_STORY - story.trim().length)} to go.</p> : null}
          </div>
          <div className="grid gap-1.5"><Label htmlFor="rv-moved">What did you deliver?</Label><Input id="rv-moved" value={moved} onChange={(e) => setMoved(e.target.value)} placeholder="What you delivered" /></div>
          <div className="grid gap-1.5"><Label htmlFor="rv-stuck">What got in the way?</Label><Input id="rv-stuck" value={stuck} onChange={(e) => setStuck(e.target.value)} placeholder="Anything we could help unblock" /></div>
          <div className="grid gap-1.5"><Label htmlFor="rv-tom">What is the plan for tomorrow?</Label><Input id="rv-tom" value={tomorrow} onChange={(e) => setTomorrow(e.target.value)} placeholder="The first thing you will pick up" /></div>
        </div>
      ) : null}
    </Frame>
  )
}

/* ------------------------------------------------------------------ planning today */

interface OutcomeDraft { text: string; minutes: number; cardId: string | null }
const blank = (): OutcomeDraft => ({ text: '', minutes: 120, cardId: null })
const STEPS = ['win', 'more', 'heads'] as const

/** Asked, never forced: a vague commitment gets one honest question, and the person decides. */
function CoachPanel({ items, onRewrite, onKeep }: { items: CommitmentCheck[]; onRewrite: () => void; onKeep: () => void }) {
  return (
    <div role="alert" className="mb-4 space-y-3 rounded-xl border border-brand-accent/40 bg-brand-softer p-4">
      <p className="flex items-center gap-2 text-sm font-semibold"><Target className="size-4 text-brand-accent" /> Make it measurable</p>
      <p className="text-sm text-text-secondary">A commitment you can judge tonight drives the day. Add a number and what done looks like.</p>
      <ul className="space-y-2.5">
        {items.map((c) => (
          <li key={c.text} className="rounded-lg bg-card p-3 text-sm">
            <p className="font-medium">“{c.text}”</p>
            <p className="mt-1 text-text-secondary">{c.issue}</p>
            {c.example ? <p className="mt-1 text-text-secondary"><span className="font-medium text-foreground">Try: </span>{c.example}</p> : null}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={onRewrite}>I will rewrite it</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onKeep}>Keep as written</Button>
      </div>
    </div>
  )
}

function PlanFlow({ gate, onDone }: { gate: Gate; onDone: () => void }) {
  const save = useSavePlan()
  const checkText = useCheckCommitments()
  // Commitments already judged specific, or kept on purpose: never asked about twice.
  const [settled, setSettled] = useState<Set<string>>(new Set())
  const [coach, setCoach] = useState<CommitmentCheck[] | null>(null)
  const goAfterCoach = useRef<(() => void) | null>(null)
  const { userId, displayName } = useIdentity()
  const { workspaceId } = useWorkspace()
  const work = useMyOpenWork(workspaceId, userId, true)
  const card = useRef<HTMLDivElement>(null)
  const [i, setI] = useState(0)
  const [outcomes, setOutcomes] = useState<OutcomeDraft[]>([blank(), blank(), blank()])
  const [attention, setAttention] = useState('')
  const [delegate, setDelegate] = useState<Array<{ text: string; person: string }>>([])
  const [tips, setTips] = useState(false)
  const queryClient = useQueryClient()
  const { can } = usePermission()
  const [creating, setCreating] = useState<{ seen: Set<string>; title: string; waiting: boolean } | null>(null)
  const boards = useQuery({ queryKey: qk.boards(workspaceId), queryFn: () => apiGet<{ items: Board[] }>('boards', { workspaceId }), enabled: Boolean(workspaceId), staleTime: 5 * 60_000 })
  const memberList = useQuery({ queryKey: qk.members(), queryFn: () => apiGet<{ items: Member[] }>('members'), staleTime: 5 * 60_000 })
  const clientList = useQuery({ queryKey: qk.clients(), queryFn: () => apiGet<{ items: Client[] }>('clients'), staleTime: 60_000 })
  const board = boards.data?.items[0]
  const people = (memberList.data?.items ?? []).filter((m) => !m.disabled)

  const which = STEPS[i] ?? 'win'
  const filled = outcomes.filter((o) => o.text.trim())
  const planned = filled.reduce((n, o) => n + Math.max(0, o.minutes), 0)
  const carry = (gate.toReview?.outcomes ?? []).filter((o) => !o.done && !outcomes.some((x) => x.text === o.text))
  const first = (displayName || '').split(' ')[0]
  const setOutcome = (n: number, patch: Partial<OutcomeDraft>) => {
    if (patch.text !== undefined) setCoach(null)
    setOutcomes((rows) => rows.map((r, j) => (j === n ? { ...r, ...patch } : r)))
  }
  /** Ask once about anything not yet judged; fail open, because advice must never stop a person planning. */
  function guarded(go: () => void) {
    const asking = filled.map((o) => o.text.trim()).filter((t) => !settled.has(t))
    if (asking.length === 0) { go(); return }
    checkText.mutate(asking, {
      onSuccess: (r) => {
        const vague = r.items.filter((x) => !x.specific)
        setSettled((s) => new Set([...s, ...r.items.filter((x) => x.specific).map((x) => x.text)]))
        if (vague.length === 0) { go(); return }
        goAfterCoach.current = go
        setCoach(vague)
      },
      onError: go,
    })
  }
  const created = creating?.waiting ? work.find((w) => !creating.seen.has(w.id)) : undefined
  if (creating && created) {
    setCreating(null)
    setOutcomes((rows) => rows.map((r, j) => (j === 0 ? { ...r, cardId: created.id, text: r.text.trim() || created.title } : r)))
  }

  function submit() {
    const rect = card.current?.getBoundingClientRect()
    save.mutate(
      {
        // Nobody is asked how long their day is. The API wants a number, so it is never less than what was planned.
        availableMinutes: Math.min(960, Math.max(480, planned)),
        outcomes: filled.map((o) => ({ text: o.text, minutes: o.minutes, cardId: o.cardId })),
        attention,
        delegate: delegate.filter((d) => d.text.trim()),
      },
      { onSuccess: () => { if (rect) flyNoteToDiary(rect, filled[0]?.text ?? 'Today'); onDone() } },
    )
  }

  const titles = {
    win: `${greeting()}${first ? `, ${first}` : ''}! What will you commit to today?`,
    more: 'Anything else you would like to get to?',
    heads: 'Anything to flag, or hand to someone?',
  } as const
  const subs = {
    win: 'Name the most important thing you will deliver today, with a number or a clear finish line so you can tell tonight whether you got there. Your team lead and the CEO see your commitments, and you will review them at the end of the day.',
    more: 'Up to two more commitments. Skip this if one is enough.',
    heads: 'Optional. Flag anything at risk or hand work over now, so your team can act on it today.',
  } as const

  const next = which === 'heads'
    ? <Button onClick={() => guarded(() => submit())} disabled={save.isPending || checkText.isPending}>Start my day</Button>
    : which === 'win'
      ? <Button onClick={() => guarded(() => setI(1))} disabled={!outcomes[0]?.text.trim() || checkText.isPending}>{checkText.isPending ? 'Checking…' : 'Next'}</Button>
      : <Button onClick={() => guarded(() => setI(2))} disabled={checkText.isPending}>{checkText.isPending ? 'Checking…' : filled.length > 1 ? 'Next' : 'Skip'}</Button>

  return (
    <>
    <Frame
      cardRef={card}
      step={i}
      steps={STEPS.length}
      kicker={`Daily diary · ${dayLabel(gate.today)}`}
      title={titles[which]}
      sub={subs[which]}
      footer={<>{i > 0 ? <BackButton onClick={() => setI(i - 1)} /> : <span />}{next}</>}
    >
      {coach && which !== 'heads' ? (
        <CoachPanel
          items={coach}
          onRewrite={() => { setCoach(null); window.setTimeout(() => document.querySelector<HTMLInputElement>('input[aria-label="My commitment for today"], input[aria-label^="Another commitment"]')?.focus(), 0) }}
          onKeep={() => { setSettled((s) => new Set([...s, ...coach.map((c) => c.text)])); setCoach(null); goAfterCoach.current?.() }}
        />
      ) : null}

      {which === 'win' ? (
        <div className="space-y-4">
          <Input aria-label="My commitment for today" autoFocus maxLength={200} value={outcomes[0]?.text ?? ''} onChange={(e) => setOutcome(0, { text: e.target.value })} placeholder="Something you can measure: Close 5 overdue TAP items by 3pm" className="h-12 text-base" />
          <div>
            <p className="mb-2 text-sm text-text-secondary">About how long will it take?</p>
            <TimeChoice label="Time for this commitment" value={outcomes[0]?.minutes ?? 120} onChange={(m) => setOutcome(0, { minutes: m })} />
          </div>
          {work.length > 0 || (board && can('create', 'Card')) ? (
            <Select value={outcomes[0]?.cardId ?? NONE} onValueChange={(v) => { if (v === CREATE) { setCreating({ seen: new Set(work.map((w) => w.id)), title: outcomes[0]?.text.trim() ?? '', waiting: false }); return } const c = work.find((w) => w.id === v); setOutcome(0, { cardId: c?.id ?? null, text: outcomes[0]?.text.trim() || c?.title || '' }) }}>
              <SelectTrigger aria-label="Link a work item" className="h-9 text-sm"><SelectValue placeholder="Link a work item (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No work item</SelectItem>
                {board && can('create', 'Card') ? <SelectItem value={CREATE}>+ Create a new work item</SelectItem> : null}
                {work.map((w) => <SelectItem key={w.id} value={w.id}>{w.code ? `${w.code} · ` : ''}{w.title}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : null}
          {carry.length > 0 && !outcomes[0]?.text.trim() ? (
            <div className="flex flex-wrap items-center gap-1.5 text-tiny text-text-secondary">
              From yesterday:
              {carry.slice(0, 3).map((c) => (
                <button key={c.id} type="button" className="rounded-full border border-border bg-card px-2.5 py-1 hover:bg-brand-soft" onClick={() => setOutcome(0, { text: c.text, cardId: c.cardId, minutes: c.minutes || 120 })}>
                  {c.text.length > 36 ? `${c.text.slice(0, 36)}…` : c.text}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {which === 'more' ? (
        <div className="space-y-4">
          {[1, 2].map((n) => (
            <div key={n} className="space-y-2">
              <Input aria-label={`Another commitment ${n}`} className="min-w-[12rem] flex-1" maxLength={200} value={outcomes[n]?.text ?? ''} onChange={(e) => setOutcome(n, { text: e.target.value })} placeholder={n === 1 ? 'Another commitment' : 'One more, if you need'} />
              {outcomes[n]?.text.trim() ? <TimeChoice label={`Time for commitment ${n + 1}`} value={outcomes[n]?.minutes ?? 120} onChange={(m) => setOutcome(n, { minutes: m })} /> : null}
            </div>
          ))}
          <p className="rounded-xl bg-brand-softer px-3 py-2 text-sm text-text-secondary" aria-live="polite">
            {filled.length > 0 ? `About ${formatMinutes(planned)} of focused work planned. Estimates are just a guide.` : 'Add something above and your estimates add up here.'}
          </p>
          <button type="button" className="text-tiny font-medium text-primary hover:underline" onClick={() => setTips((t) => !t)}>{tips ? 'Hide' : 'Need help choosing?'}</button>
          {tips ? (
            <ol className="list-decimal space-y-0.5 pl-5 text-tiny text-text-secondary">
              <li>Does it matter to a customer or to revenue?</li>
              <li>Is there a real deadline?</li>
              <li>Does it unblock other people?</li>
              <li>Are you the best person for it?</li>
              <li>Could someone else take it, or could it wait?</li>
            </ol>
          ) : null}
        </div>
      ) : null}

      {which === 'heads' ? (
        <div className="space-y-5">
          <div className="grid gap-1.5">
            <Label htmlFor="dg-attn">Anything that needs attention soon?</Label>
            <Textarea id="dg-attn" rows={2} value={attention} onChange={(e) => setAttention(e.target.value)} placeholder="A deadline, a customer, a blocker" />
          </div>
          <div className="grid gap-1.5">
            <Label>Handing anything to someone?</Label>
            <div className="space-y-1.5">
              {delegate.map((d, n) => (
                <div key={n} className="flex gap-1.5">
                  <Input aria-label="Task to hand over" value={d.text} onChange={(e) => setDelegate((r) => r.map((x, j) => (j === n ? { ...x, text: e.target.value } : x)))} placeholder="Task" />
                  <div className="w-44 shrink-0">
                    <Select value={people.find((m) => m.displayName === d.person)?.id ?? NONE} onValueChange={(v) => setDelegate((r) => r.map((x, j) => (j === n ? { ...x, person: people.find((m) => m.id === v)?.displayName ?? '' } : x)))}>
                      <SelectTrigger aria-label="Who" className="h-9"><SelectValue placeholder="Who" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Choose someone</SelectItem>
                        {people.filter((m) => m.id !== userId).map((m) => <SelectItem key={m.id} value={m.id}>{m.displayName}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <button type="button" aria-label="Remove" className="grid size-9 shrink-0 place-items-center rounded-md hover:bg-muted" onClick={() => setDelegate((r) => r.filter((_, j) => j !== n))}><X className="size-4" /></button>
                </div>
              ))}
              {delegate.length < 5 ? <Button type="button" variant="ghost" size="sm" onClick={() => setDelegate((r) => [...r, { text: '', person: '' }])}><Plus className="size-3.5" /> Add one</Button> : null}
            </div>
          </div>
        </div>
      ) : null}
    </Frame>
      {creating && !creating.waiting && board ? (
        <NewWorkItemDialog
          key={creating.title}
          boardId={board.id}
          workspaceId={workspaceId}
          members={people}
          clients={clientList.data?.items ?? []}
          defaultOwnerId={userId}
          prefill={{ title: creating.title, description: '' }}
          trigger={<span className="hidden" aria-hidden="true" />}
          onPrefillConsumed={() => {
            setCreating((c) => (c ? { ...c, waiting: true } : c))
            void queryClient.invalidateQueries({ queryKey: ['diary', 'my-open-work'] })
            window.setTimeout(() => setCreating(null), 8000)
          }}
        />
      ) : null}
    </>
  )
}
